import { useState } from 'react';
import { PencilIcon, Trash2Icon, UserPlusIcon } from 'lucide-react';
import { TextField } from '../Field';
import type { CoInvestigator } from '../../types/portal';

interface CoInvestigatorsProps {
  items: CoInvestigator[];
  onChange: (items: CoInvestigator[]) => void;
}

const blank = { name: '', title: '', department: '', contact: '' };

export function CoInvestigators({ items, onChange }: CoInvestigatorsProps) {
  const [draft, setDraft] = useState(blank);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof blank) => (value: string) =>
  setDraft((prev) => ({ ...prev, [key]: value }));

  const submit = () => {
    if (!draft.name.trim() || !draft.title.trim()) {
      setError('Name and title/position are required for a co-investigator.');
      return;
    }
    setError(null);
    if (editingId) {
      onChange(
        items.map((item) => item.id === editingId ? { ...item, ...draft } : item)
      );
      setEditingId(null);
    } else {
      onChange([...items, { id: `ci${Date.now()}`, ...draft }]);
    }
    setDraft(blank);
  };

  const startEdit = (item: CoInvestigator) => {
    setEditingId(item.id);
    setDraft({
      name: item.name,
      title: item.title,
      department: item.department,
      contact: item.contact
    });
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      <div className="table-scroll">
        <table className="data-table">
          <caption className="sr-only">Co-investigators</caption>
          <thead>
            <tr>
              <th scope="col" className="col-sr">
                Sr
              </th>
              <th scope="col">Name</th>
              <th scope="col">Title / Position</th>
              <th scope="col">Institute / Organization</th>
              <th scope="col">Contact no.</th>
              <th scope="col" className="col-action">
                Edit
              </th>
              <th scope="col" className="col-action">
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ?
            <tr>
                <td colSpan={7}>
                  <p className="table-empty">
                    No co-investigators added. Use the form below if your study has any.
                  </p>
                </td>
              </tr> :

            items.map((item, index) =>
            <tr key={item.id}>
                  <td className="col-sr">{index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.title}</td>
                  <td>{item.department || '—'}</td>
                  <td>{item.contact || '—'}</td>
                  <td className="col-action">
                    <button
                  type="button"
                  className="btn-icon"
                  aria-label={`Edit ${item.name}`}
                  onClick={() => startEdit(item)}>
                  
                      <PencilIcon size={15} aria-hidden="true" />
                    </button>
                  </td>
                  <td className="col-action">
                    <button
                  type="button"
                  className="btn-icon btn-icon--danger"
                  aria-label={`Delete ${item.name}`}
                  onClick={() => onChange(items.filter((i) => i.id !== item.id))}>
                  
                      <Trash2Icon size={15} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
            )
            }
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {editingId ? 'Edit co-investigator' : 'Add co-investigator'}
          </h3>
          {editingId ?
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setEditingId(null);
              setDraft(blank);
            }}>
            
              Cancel edit
            </button> :
          null}
        </div>
        <div className="card-body">
          <div className="grid-2">
            <TextField
              id="ci-name"
              label="Name"
              required
              value={draft.name}
              onChange={set('name')} />
            
            <TextField
              id="ci-title"
              label="Title / Position"
              required
              value={draft.title}
              onChange={set('title')} />
            
            <TextField
              id="ci-department"
              label="Department"
              value={draft.department}
              onChange={set('department')} />
            
            <TextField
              id="ci-contact"
              label="Contact no."
              type="tel"
              value={draft.contact}
              onChange={set('contact')} />
            
          </div>
          {error ?
          <p className="error-text" style={{ marginTop: 'var(--space-3)' }} role="alert">
              {error}
            </p> :
          null}
          <button
            type="button"
            className="btn btn--primary"
            style={{ marginTop: 'var(--space-4)' }}
            onClick={submit}>
            
            <UserPlusIcon size={16} aria-hidden="true" />
            {editingId ? 'Save Co-Investigator' : 'Add Co-Investigator'}
          </button>
        </div>
      </div>
    </div>);

}